import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useMemberIDCardFax } from '@sentara-health/sentara-api-hooks-core';
import { errorhandler, getMaxLength, NoSpecialCharacter, OnlyNumbers, isUnAuthorizedError, API_STATUS_SUCCESS, domainForSegment } from 'common';
import { useAdobeAnalytics } from '@sentara-health/platform-analytics';
import {
  ErrorMessage,
  Button,
  ModalOverlay,
  Headings,
  InputField,
  Label,
  TextArea
} from '@sentara-health/optimahealth_web';
import { GlobalContext } from 'context';
type FormInputs = {
  recipientPhone: string;
  recipientName: string;
  comments: string;
}

function MemberIdCardFax({ setIsOpen, isOpen, setIsOpenFax }: any) {
  //  Initialize useTranslation hook
  const { t } = useTranslation();
  const { selectedIdCardMemberId } = useContext(GlobalContext); 
  const { contentListingItemClicked } = useAdobeAnalytics();

  const { handleSubmit, register, reset, setValue, formState: { errors, isValid } } = useForm<FormInputs>({}); /* useForm props extract */

  const [isFaxSuccess, setIsFaxSuccess] = useState<boolean>(false);
  const [isFaxError, setIsFaxError] = useState<boolean>(false);

  // Initializing the memberIDFaxType hook to fetch member ID fax type
  const { memberIDCardFaxType } = useMemberIDCardFax();

  const trackFaxSubmit = () => {
    contentListingItemClicked({
      segment: `${domainForSegment}${globalThis.location.pathname}`,
      contentListing: {
        eventType: 'Content Listing Item Clicked',
        listing: {
          content: [
            {
              contentId: 303 as unknown as string,
              contentTitle: 'Member_ID_card_fax_click',
              itemPosition: 3,
              ...({
                interactionSource: 'Member ID Card Details Page',
              } as Record<string, string>),
            },
          ],
        },
      },
    });
  };

  // Function to handle form submission
  const saveData = async (data: FormInputs) => {
    trackFaxSubmit();
    const payload = {
      memberId: selectedIdCardMemberId,
      recipientPhoneNumber: data?.recipientPhone,
      recipientName: data?.recipientName,
      comment: data?.comments
    };
 
    const response = await memberIDCardFaxType(payload);
    
    if (response?.status === Number(API_STATUS_SUCCESS)) {
      setIsFaxSuccess(true);
    } else if (response?.errorCode) {
      if (isUnAuthorizedError(response.errorCode)) {
        errorhandler(response.errorCode); // 401 unauthorized error
      } else {
        setIsFaxError(true);
        setIsFaxSuccess(false);
      }
    } else {
      onClose(); // close the popup on success
    }

  };
  /* onclose function to close popup and reset the fields to blank on load */
  const onClose = () => {
    reset();
    setIsOpen(false);
  };

  const onCloseFaxSuccess = () => {
    setIsFaxSuccess(false);
    setIsFaxError(false);
    onClose();
  };  

  return (
    <div className="modal_popup_login">
      {isFaxSuccess ? (
        <ModalOverlay
          isOpen={isFaxSuccess}
          onClose={onCloseFaxSuccess}
          className="modal-w-medium p-10"
        >
          <Headings
              text={t('memberIdCard.memberIdFaxSuccessHeading')}
              dataTestId="memberIdFaxHeading"
              variant="semiBold"
            />
          <div className="mb-3 position-relative">
            { t('memberIdCard.memberIdFaxSuccessMessage')}              
          </div>
          <div className="row g-3">
            <div className="col-12">
            <Button
              dataTestId={'okButton'}
              type="submit"
              variant="primary_btn"
              className="btn-sm-height"
              onClick={onCloseFaxSuccess}
            >{t('common.buttonOk')}</Button>
            </div>
          </div>
        </ModalOverlay>
      ) : (
        <ModalOverlay
          isOpen={isOpen}
          onClose={onClose}
          overlayInfo="Member ID card- Fax information overlay"
          className="modal-w-medium p-10"
        >
          <Headings
              text={t('memberIdCard.memberIdFaxHeading')}
              dataTestId="memberIdFaxHeading"
              variant="semiBold"
            />
          <form onSubmit={handleSubmit(saveData)} autoComplete="off">

            <div className="mb-3 position-relative">
              <Label className="pb-1" dataTestId={'recipientNameLabel'}
                htmlFor="recipientName"
              >
                {t('activation.recipientName')}
              </Label>
              <span className="text-danger "> *</span>
              <InputField
                id="recipientName"
                className={errors.recipientName ? 'error-border form-input' : 'form-input'}
                placeholder={t('memberIdCard.recipientName')}
                data-testid="recipientNameInput"
                {...register('recipientName', {
                  required: true,
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                    setValue('recipientName', e.target.value.replace(NoSpecialCharacter, ''));
                  }
                })}
              />
              {errors.recipientName && (
                <ErrorMessage dataTestId="reciepientNameInputError">
                  {t('memberIdCard.reciepientNameRequired')}
                </ErrorMessage>
              )}

            </div>
            <div className="mb-3 position-relative">
              <Label
                className="pb-1"
                dataTestId={'recipientPhoneLabel'}
                htmlFor="recipientPhone"
              >{t('common.phoneNo')}</Label>
              <span className="text-danger "> *</span>
              <InputField
                id="recipientPhone"
                maxLength={10}
                className={(errors.recipientPhone || isFaxError) ? 'error-border form-input' : 'form-input'}
                placeholder={t('memberIdCard.PhoneNumberPlaceHolder')}
                type='text'
                data-testid="recipientPhoneInput"
                {...register('recipientPhone', {
                  required: true,
                  maxLength: getMaxLength?.maxPhoneNumber,
                  minLength: getMaxLength?.maxPhoneNumber,
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                    setValue('recipientPhone', e.target.value.replace(OnlyNumbers, ''));
                    setIsFaxError(false);
                  }
                })}
              />
              {errors.recipientPhone && (
                <ErrorMessage dataTestId="phoneNumberInputError">
                  {t('memberIdCard.phoneNumberRequired')}
                </ErrorMessage>
              )}
              {isFaxError && (
                <ErrorMessage dataTestId="phoneNumberFaxError">
                  {t('memberIdCard.memberIdFaxInlineErrorMessage')}
                </ErrorMessage>
              )}
            </div>

            <div className="mb-3 position-relative">
              <Label
                className="pb-1"
                dataTestId={'commentsInputLabel'}
                htmlFor="commentsInput1"
              >
                {t('activation.comment')}
              </Label>

              <TextArea
                data-testid="commentsInput"
                className="form-input"
                placeholder={t('memberIdCard.comment')}
                id="comment"
                rows={4}
                {...register('comments', {
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                    setValue('comments', e.target.value);
                  }
                })}
              />
            </div>
            <div className="row g-3">
              <div className='col-sm-6 col-12'>
              <Button
                dataTestId={'continueButton'}
                type="submit"
                variant="primary_btn"
                disabled={!isValid}
                className="btn-sm-height"
              >{t('memberIdCard.sendFax')}</Button>
              </div>
              <div className='col-sm-6 col-12'>
              <Button
                dataTestId={'cancelButton'}
                type="button"
                variant="secondary"
                className="btn-sm-height"
                onClick={onCloseFaxSuccess}
              >{t('common.cancel')}</Button>
              </div>
              </div>
          </form>
        </ModalOverlay>
      )};
    </div>
  );
}

export default MemberIdCardFax;